import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const TrackOrder: React.FC = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [trackingResult, setTrackingResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Mock tracking data
  const mockTrackingData = {
    orderNumber: 'ORD-2024-001',
    status: 'In Transit',
    estimatedDelivery: '2024-01-25',
    trackingNumber: '1Z999AA1234567890',
    carrier: 'FedEx',
    timeline: [
      {
        date: '2024-01-20',
        time: '10:30 AM',
        status: 'Order Placed',
        description: 'Your order has been confirmed and is being processed',
        icon: CheckCircleIcon,
        completed: true
      },
      {
        date: '2024-01-21',
        time: '2:15 PM',
        status: 'Processing',
        description: 'Your design is being prepared for production',
        icon: CheckCircleIcon,
        completed: true
      },
      {
        date: '2024-01-22',
        time: '9:45 AM',
        status: 'Shipped',
        description: 'Your order has been shipped via FedEx',
        icon: TruckIcon,
        completed: true
      },
      {
        date: '2024-01-23',
        time: '3:20 PM',
        status: 'In Transit',
        description: 'Package is on its way to you',
        icon: ClockIcon,
        completed: false
      },
      {
        date: '2024-01-25',
        time: 'Estimated',
        status: 'Delivered',
        description: 'Package will be delivered to your address',
        icon: CheckCircleIcon,
        completed: false
      }
    ]
  };

  const handleTrackOrder = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      if (orderNumber === 'ORD-2024-001' && email === 'test@example.com') {
        setTrackingResult(mockTrackingData);
      } else {
        setTrackingResult({ error: 'Order not found. Please check your order number and email.' });
      }
      setIsLoading(false);
    }, 1000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Delivered':
        return 'bg-green-100 text-green-800';
      case 'In Transit':
        return 'bg-blue-100 text-blue-800';
      case 'Shipped':
        return 'bg-purple-100 text-purple-800';
      case 'Processing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'In Transit':
        return <TruckIcon className="w-5 h-5" />;
      case 'Shipped':
        return <TruckIcon className="w-5 h-5" />;
      case 'Processing':
        return <ClockIcon className="w-5 h-5" />;
      default:
        return <ClockIcon className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Track Your Order</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Stay updated on your order status and delivery progress
          </p>
        </div>
      </section>

      {/* Tracking Form */}
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Track Your Order</h2>
            
            <form onSubmit={handleTrackOrder} className="space-y-6">
              <div>
                <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Order Number
                </label>
                <input
                  type="text"
                  id="orderNumber"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., ORD-2024-001"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Tracking...
                  </>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                    Track Order
                  </>
                )}
              </button>
            </form>

            {/* Demo Info */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Demo:</strong> Use Order Number "ORD-2024-001" and Email "test@example.com" to see tracking information.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tracking Results */}
      {trackingResult && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {trackingResult.error ? (
              <div className="bg-white rounded-lg shadow-lg p-8 text-center">
                <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h3>
                <p className="text-gray-600">{trackingResult.error}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {/* Order Summary */}
                <div className="bg-gray-50 p-6 border-b border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Order Number</p>
                      <p className="font-semibold text-gray-900">{trackingResult.orderNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(trackingResult.status)}`}>
                        {getStatusIcon(trackingResult.status)}
                        <span className="ml-1">{trackingResult.status}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Estimated Delivery</p>
                      <p className="font-semibold text-gray-900">{trackingResult.estimatedDelivery}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Carrier</p>
                      <p className="font-semibold text-gray-900">{trackingResult.carrier}</p>
                    </div>
                  </div>
                </div>

                {/* Tracking Timeline */}
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Tracking Timeline</h3>
                  <div className="space-y-6">
                    {trackingResult.timeline.map((item: any, index: number) => (
                      <div key={index} className="flex items-start">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          item.completed ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          <item.icon className={`w-4 h-4 ${item.completed ? 'text-white' : 'text-gray-500'}`} />
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-semibold text-gray-900">{item.status}</h4>
                            <p className="text-sm text-gray-500">{item.date} {item.time}</p>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tracking Number */}
                <div className="bg-blue-50 p-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Tracking Number</h4>
                  <p className="font-mono text-blue-600">{trackingResult.trackingNumber}</p>
                  <p className="text-sm text-gray-600 mt-1">
                    You can also track your package directly on the {trackingResult.carrier} website.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Help Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Need Help?</h2>
          <p className="text-gray-600 mb-8">
            Can't find your order or need assistance? Our customer support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors text-center"
            >
              Contact Support
            </Link>
            <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors">
              View Order History
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrackOrder; 